import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useTenancy } from '@/contexts/TenancyContext';
import { db } from '@/js/external-js/config/main_config';
import { Timestamp } from 'firebase/firestore';
import documentationService, {
  DocumentationData,
  Policy,
  SupportingDocument,
  KeyDocument
} from '@/services/documentationService';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Separator } from "@/components/ui/separator";
import { Pencil, FileText, File, FolderOpen } from 'lucide-react';

import { getAllModules } from '@/app/pages/msg/utils/common';
import { fetchRelatedDocuments, populateCustomContent, fetchPageContentMDX } from '@/app/pages/msg/utils/common';
import { MarkdownRenderer } from '@/app/pages/msg/components/MarkdownRenderer';
import { useMarkdownComponents } from '../../components/MDXFields';
import { renderDate } from '../../../../../../shared/renderDate';
import AdditionalContent from '../../components/AdditionalContent';
import FootNote from '../../components/FootNote';
import { Accordion } from '@radix-ui/react-accordion';
import SupportingDocs from '../../components/SupportingDocs';
import RelatedClausesAccordianItem from '../../components/RelatedClauses';
import SupportingDocsDropdown from '../../components/SupportingDocsDropdown';

const DocumentationSection = () => {
  const { activeTenancy } = useTenancy();
  const clauseStandards = useRef<Set<string>>(new Set());
  const [pageClauses, setPageClauses] = useState<Set<string>>(new Set());
  const updatePageClauses = (newClauses: Set<string>) => {
    setPageClauses(new Set(newClauses));
  };

  const [pageTemplate, setPageTemplate] = React.useState<string>('');
  const [supportingDocs, setSupportingDocs] = React.useState<any[]>([]);
  const [dynamicContent, setDynamicContent] = React.useState<string>('');
  const [modulesList, setModulesList] = useState<[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [documentation, setDocumentation] = useState<DocumentationData>({
    policies: [],
    supportingDocuments: [],
    keyDocuments: []
  });

  const pageName = 'support';
  const contentPath = 'documentation';
  const pageRoute = '/MSG/support/documentation';

  // Get markdown components (must be called unconditionally at top level)
  const markdownComponents = useMarkdownComponents(modulesList, clauseStandards, { current: pageClauses }, updatePageClauses);

  // Load documentation data
  const loadDocumentation = useCallback(async (companyID: string) => {
    if (!companyID) return;

    try {
      setIsLoading(true);
      const data = await documentationService.fetchAllDocumentation(companyID);
      setDocumentation(data);
    } catch (error) {
      console.error('Error loading documentation:', error);
      // Handle error (e.g., show toast)
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Setup real-time listener for documentation changes
  useEffect(() => {
    if (!activeTenancy.id) return;

    const unsubscribe = documentationService.onDocumentationChange(
      activeTenancy.id,
      (data: DocumentationData) => {
        setDocumentation(data);
      }
    );

    // Initial load
    loadDocumentation(activeTenancy.id);

    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, [activeTenancy.id, loadDocumentation]);

  // Load page content and modules
  useEffect(() => {
    if (!activeTenancy.id) return;

    const loadContent = async () => {
      try {
        await Promise.all([
          fetchPageContentMDX(db, pageName, contentPath, activeTenancy, setPageTemplate),
          // fetchRelatedDocuments(db, activeTenancy.id, pageRoute, (docs: any[]) => {
          //   setSupportingDocs(docs);
          // }),
          populateCustomContent(db, activeTenancy.id, pageRoute, (content: string) => {
            setDynamicContent(content);
          })
        ]);
      } catch (error) {
        console.error('Error loading page content:', error);
      }
    };

    const loadModules = async () => {
      try {
        const allModules = await getAllModules();
        setModulesList(allModules);
      } catch (error) {
        console.error('Error loading modules:', error);
        setModulesList([]);
      }
    };

    loadContent();
    loadModules();

    // Cleanup function
    return () => {
      // Any cleanup code if needed
    };
  }, [activeTenancy.id, activeTenancy, pageName, contentPath, pageRoute]);

  // Format date helper
  const formatDate = (date: Date | Timestamp | undefined): string => {
    if (!date) return 'N/A';
    const dateObj = date instanceof Date ? date : date.toDate();
    return dateObj.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Render loading state
  if (isLoading) {
    return (
      <div className="space-y-6 p-4">
        <Skeleton className="h-10 w-64 mb-6" />
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="w-full">
              <CardHeader>
                <Skeleton className="h-6 w-48" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-4 w-full mb-2" />
                <Skeleton className="h-4 w-3/4" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 p-6">
      <div className="bg-card rounded-xl border border-border shadow-sm relative">
        {/* Main content */}
        {pageTemplate && (
          <div className="prose dark:prose-invert max-w-none mb-8">
            <div className="absolute right-4 top-4 z-10">
              <div className="flex items-center space-x-2">
                <SupportingDocsDropdown pageRoute={pageRoute} clauseStandards={clauseStandards} pageClauses={pageClauses} />
                <RelatedClausesAccordianItem modulesList={modulesList} clauseStandards={clauseStandards} setModulesList={setModulesList} />
              </div>
            </div>
            <MarkdownRenderer source={pageTemplate} components={markdownComponents} />
            <div style={{ maxWidth: '56rem', marginLeft: 'auto', marginRight: 'auto', padding: '0px 1rem 1.5rem', transition: 'color 0.2s, background-color 0.2s', backgroundColor: 'transparent' }}>
              <AdditionalContent pageRoute={pageRoute} />
              <FootNote />
            </div>
          </div>
        )}
      </div>

      <div className="bg-card rounded-xl border border-border shadow-sm space-y-8 p-6">
        {/* Policies Section */}
        <section className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-semibold flex items-center">
              <FileText className="mr-2 h-6 w-6 text-primary" />
              Policies
            </h2>
            <span className="text-sm text-muted-foreground">
              {documentation.policies.length} {documentation.policies.length === 1 ? 'policy' : 'policies'}
            </span>
          </div>

          <div className="grid gap-2 md:grid-cols-3 sm:grid-cols-2 grid-cols-1">
            {documentation.policies.length > 0 ? (
              documentation.policies.map((policy) => (
                <Card key={policy.id} className="hover:shadow-md transition-shadow max-h-[80px] p-2">
                  <CardHeader className="pb-1 px-2">
                    <CardTitle className="text-[0.97rem] font-medium line-clamp-2">
                      {policy.policyName}
                    </CardTitle>
                    <p className="text-xs text-muted-foreground mt-1">
                      Last reviewed: {renderDate(policy.policy_history.last_reviewed)}
                    </p>
                  </CardHeader>
                </Card>
              ))
            ) : (
              <div className="col-span-full py-8 text-center text-muted-foreground">
                <FolderOpen className="mx-auto h-12 w-12 mb-2 opacity-30" />
                <p>No policies found</p>
              </div>
            )}
          </div>
        </section>
      </div>

      <div className="bg-card rounded-xl border border-border shadow-sm space-y-8 p-6">
        {/* Supporting Documents Section */}
        <section className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-semibold flex items-center">
              <File className="mr-2 h-6 w-6 text-primary" />
              Supporting Documents
            </h2>
            <span className="text-sm text-muted-foreground">
              {documentation.supportingDocuments.length} {documentation.supportingDocuments.length === 1 ? 'document' : 'documents'}
            </span>
          </div>

          <div className="grid gap-2 md:grid-cols-3 sm:grid-cols-2 grid-cols-1">
            {documentation.supportingDocuments.length > 0 ? (
              documentation.supportingDocuments.map((doc) => (
                <Card key={doc.id} className="hover:shadow-md transition-shadow max-h-[80px] p-2">
                  <CardHeader className="pb-1 px-2">
                    <CardTitle className="text-[0.97rem] font-medium line-clamp-2">
                      {doc.fileName}
                    </CardTitle>
                    <p className="text-xs text-muted-foreground mt-1">
                      Added: {renderDate(doc.dateUploaded)}
                    </p>
                  </CardHeader>
                </Card>
              ))
            ) : (
              <div className="col-span-full py-8 text-center text-muted-foreground">
                <FolderOpen className="mx-auto h-12 w-12 mb-2 opacity-30" />
                <p>No supporting documents found</p>
              </div>
            )}
          </div>
        </section>
      </div>

      {/* <div className="bg-card rounded-xl border border-border shadow-sm space-y-8 p-6">
        <section className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-semibold flex items-center">
              <File className="mr-2 h-6 w-6 text-primary" />
              Key Documents
            </h2>
            <span className="text-sm text-muted-foreground">
              {documentation.keyDocuments.length} {documentation.keyDocuments.length === 1 ? 'document' : 'documents'}
            </span>
          </div>

          <div className="grid gap-2 md:grid-cols-3 sm:grid-cols-2 grid-cols-1">
            {documentation.keyDocuments.length > 0 ? (
              documentation.keyDocuments.map((doc) => (
                <Card key={doc.id} className="hover:shadow-md transition-shadow">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg line-clamp-2">
                      {doc.cardTitle}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">
                      Updated: {formatDate(doc.dateUpdated)}
                    </p>
                  </CardContent>
                </Card>
              ))
            ) : (
              <div className="col-span-full py-8 text-center text-muted-foreground">
                <FolderOpen className="mx-auto h-12 w-12 mb-2 opacity-30" />
                <p>No key documents found</p>
              </div>
            )}
          </div>
        </section>
      </div> */}
      {/* <div className="bg-card rounded-xl border border-border shadow-sm">
        <SupportingDocs pageRoute={pageRoute} clauseStandards={clauseStandards} pageClauses={pageClauses} />
      </div> */}
    </div>
  );
}

export default DocumentationSection;
